import * as React from 'react';

import cn from 'classnames';
import MaskedInput from 'react-text-mask';

import { EElementPosition } from '@enums';

import { noop } from '@utils';

import { Translate } from '@decorators';

import { InputField } from './InputField';
import { InputGroup } from './InputGroup';

import { IInputState, IInputProps } from './types';

import * as s from './Input.scss';

const STATIC_VALUE_OFFSET_ALLOWANCE = 1;

@Translate('base.components.ui.Input')
class InputWithoutRef extends React.Component<IInputProps, IInputState> {

	staticValueRef: React.RefObject<HTMLLabelElement> = React.createRef();

	static defaultProps: Partial<IInputProps> = {
		className: '',
		autoFocus: false,
		multiline: false,
		isValid: true,
		onBlur: noop,
		onFocus: noop,
		onKeyDown: noop,
		onKeyUp: noop,
		descriptionPosition: EElementPosition.BOTTOM,
		dataAttributes: {},
		guide: true,
		type: 'text',
	};

	readonly state: IInputState = {
		value: '',
		paddingLeft: 0,
	};

	componentDidMount() {
		if (this.staticValueRef.current) {
			const { offsetWidth, offsetLeft } = this.staticValueRef.current;

			this.setState({ paddingLeft: offsetWidth + offsetLeft - STATIC_VALUE_OFFSET_ALLOWANCE });
		}
	}

	getValue() {
		return this.props.value || this.state.value;
	}

	protected onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { onChange } = this.props;

		if (onChange) {
			onChange(event);

			return;
		}

		this.setState({
			value: event.target.value,
		});
	};

	render() {
		const {
			innerRef,
			autoFocus,
			className,
			classNameInput,
			classNameCounter,
			description,
			descriptionPosition,
			disabled,
			error,
			id,
			isValid,
			label,
			limit,
			multiline,
			name,
			onBlur,
			onFocus,
			onKeyDown,
			onKeyUp,
			placeholder,
			rows,
			withCounter,
			staticValue,
			mask,
			rightElement,
			dataAttributes,
			testId,
			guide,
			keepCharPositions,
			showMask,
			pipe,
			placeholderChar,
			type,
			autocomplete,
		} = this.props;
		const { paddingLeft } = this.state;
		const inputFieldStyle = paddingLeft
			? { paddingLeft: `${paddingLeft}px` }
			: {};

		return (
			<InputGroup
				className={className}
				isValid={isValid}
				error={error}
				label={label}
				description={description}
				descriptionPosition={descriptionPosition}
				id={id}
				rightElement={rightElement}
			>
				{staticValue && (
					<label
						ref={this.staticValueRef}
						htmlFor={id}
						className={s.staticText}
					>
						{staticValue}
					</label>
				)}
				<InputField
					style={inputFieldStyle}
					ref={innerRef}
					className={classNameInput}
					autoFocus={autoFocus}
					disabled={disabled}
					id={id}
					isValid={isValid}
					maxLength={limit}
					multiline={multiline}
					name={name}
					onBlur={onBlur}
					onChange={this.onChange}
					onFocus={onFocus}
					onKeyDown={onKeyDown}
					onKeyUp={onKeyUp}
					placeholder={placeholder}
					rows={rows}
					value={this.getValue()}
					mask={mask}
					dataAttributes={dataAttributes}
					testId={testId}
					guide={guide}
					keepCharPositions={keepCharPositions}
					pipe={pipe}
					placeholderChar={placeholderChar}
					showMask={showMask}
					type={type}
					autocomplete={autocomplete}
				/>
				{
					withCounter &&
					<div className={cn(s.counter, classNameCounter)}>
						<span>{this.getValue().length}</span>
						{
							limit &&
							<span> / {limit}</span>
						}
						<span className={s.symbolsLabel}>{this.t('symbols')}</span>
					</div>
				}
			</InputGroup>
		);
	}

}

export const Input = React.forwardRef(
	(
		props: IInputProps,
		ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement | MaskedInput>,
	) => <InputWithoutRef innerRef={ref} {...props} />,
);
